import type { PlanTier } from "@/lib/authz/plans";

export const WORKSHOP_STORE_URL = "https://space101.store/pages/workshop-v2";
// Shopify hosted customer portal — where an existing subscriber manages, swaps,
// or cancels their subscription. Use this (not a new checkout) for upgrades so
// we never create a second concurrent subscription.
export const SHOPIFY_CUSTOMER_PORTAL =
  "https://shopify.com/68149903680/account/pages/39847321-8f67-47af-86b8-dd623b8e9d8e";
export const DISCORD_URL = "https://discord.gg/theworkshop";

/** Where the "Get more tokens" CTAs point — the live Shopify token-pack product. */
export const BUY_TOKENS_URL = "https://space101.store/products/tokens";

const PLAN_TIER_TO_UTM_CONTENT: Record<PlanTier, string> = {
  free: "free",
  paintbooth: "paint-booth",
  vehicle_dev: "vehicle-dev",
  server_dev: "server-dev",
  all_services: "all-services",
  studio: "studio",
};

export function buildSubscribeUrl(
  planTier: PlanTier,
  baseUrl: string = WORKSHOP_STORE_URL,
): string {
  const url = new URL(baseUrl);
  url.searchParams.set("utm_source", "theworkshop");
  url.searchParams.set("utm_medium", "referral");
  url.searchParams.set("utm_campaign", "workshop_subscribe");
  url.searchParams.set("utm_content", PLAN_TIER_TO_UTM_CONTENT[planTier]);
  return url.toString();
}

export const landingNavLinks = [
  { label: "Tools", href: "#tools" },
  { label: "Workflow", href: "#workflow" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export const heroFileBadges = [".yft", ".ytd", ".meta", ".psd", ".png"];

export type HeroIntent = {
  key: "livery" | "meta";
  label: string;
  routeLabel: string;
  files: string[];
  tool: string;
  output: string;
  accent: "clay" | "amber" | "cream";
  /** Optional iframe src for the hero animation. Defaults to /workshop-loop.html. */
  loopSrc?: string;
};

export const heroIntents: HeroIntent[] = [
  {
    key: "livery",
    label: "Live view livery edits",
    routeLabel: "Paint Booth live texture pass",
    files: ["livery.psd", "vehicle.yft", "shared.ytd"],
    tool: "Paint Booth",
    output: "3D preview ready",
    accent: "clay",
  },
  {
    key: "meta",
    label: "Render vehicle templates",
    routeLabel: "UV Studio template render",
    files: ["vehicle.yft", "vehicle.ytd", "template.png"],
    tool: "UV Studio",
    output: "template.png exported",
    accent: "cream",
    loopSrc: "/uv-studio-loop.html",
  },
];

// The day the hand-written `value` figures below were accurate. Daily growth is
// accumulated forward from here, so bumping a `value` means bumping this too.
const LANDING_STATS_EPOCH = "2026-08-31";

export const landingStats = [
  // `growth` → the stat drifts up every day by a random amount inside
  // [min, max], seeded off the date so every visitor (and the server render)
  // sees the same figure on the same day and it never ticks backwards.
  { value: "7.1K+", label: "creator community", growth: { min: 6, max: 18 } },
  // `dynamic: "toolCount"` → resolved from LANDING_TOOL_COUNT at render time so
  // this never needs hand-updating when a tool is added/removed. `value` is the
  // fallback if the dynamic count is ever unavailable. Deliberately has no
  // `growth`: it's a real count, not a drifting one.
  { value: "18", label: "browser-native tools", dynamic: "toolCount" as const },
  { value: "14.3K+", label: "projects completed", growth: { min: 22, max: 61 } },
  { value: "28.9K+", label: "hours saved", growth: { min: 48, max: 132 } },
];

export type LandingStat = (typeof landingStats)[number];

/** "7.1K+" → { amount: 7100, unit: "K", plus: "+" } */
function parseStatFigure(raw: string) {
  const match = raw.match(/^(\d+(?:\.\d+)?)(K|M)?(\+?)$/i);
  if (!match) return null;
  const unit = (match[2] ?? "").toUpperCase() as "" | "K" | "M";
  const scale = unit === "M" ? 1_000_000 : unit === "K" ? 1_000 : 1;
  return { amount: parseFloat(match[1]) * scale, unit, plus: match[3] };
}

function formatStatFigure(amount: number, unit: "" | "K" | "M", plus: string) {
  const scale = unit === "M" ? 1_000_000 : unit === "K" ? 1_000 : 1;
  const scaled = amount / scale;
  // Keep the original shape: "7.1K+" stays one decimal, a plain "18" stays whole.
  return `${unit ? scaled.toFixed(1) : Math.round(scaled).toString()}${unit}${plus}`;
}

/** Deterministic 0–1 from a (seed, day) pair — mulberry32's mixing step. */
function seededUnit(seed: number, day: number) {
  let t = (seed + Math.imul(day, 0x6d2b79f5)) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function hashSeed(text: string) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

const DAY_MS = 86_400_000;
// Sanity ceiling so a bad clock (or a very stale epoch) can't spin the loop or
// balloon the figure — ~27 years of drift is far past when we'd reset the epoch.
const MAX_GROWTH_DAYS = 10_000;

/**
 * Resolve a stat's display string for a given day: the base figure plus one
 * randomised increment per elapsed day since the epoch. Same day in, same
 * string out — no hydration drift, no numbers going down.
 */
export function resolveLandingStatValue(stat: LandingStat, now: Date = new Date()): string {
  if (!("growth" in stat) || !stat.growth) return stat.value;
  const figure = parseStatFigure(stat.value);
  if (!figure) return stat.value;

  const epochDay = Math.floor(Date.parse(`${LANDING_STATS_EPOCH}T00:00:00Z`) / DAY_MS);
  const today = Math.floor(now.getTime() / DAY_MS);
  const elapsed = Math.min(Math.max(today - epochDay, 0), MAX_GROWTH_DAYS);
  if (elapsed === 0) return stat.value;

  const seed = hashSeed(stat.label);
  const { min, max } = stat.growth;
  let amount = figure.amount;
  for (let day = 1; day <= elapsed; day += 1) {
    amount += min + Math.floor(seededUnit(seed, day) * (max - min + 1));
  }
  return formatStatFigure(amount, figure.unit, figure.plus);
}

export const trustedDeveloperLogos: Array<{ name: string; src: string | null }> = [
  { name: "Developer logo slot 1", src: null },
  { name: "Developer logo slot 2", src: null },
  { name: "Developer logo slot 3", src: null },
  { name: "Developer logo slot 4", src: null },
  { name: "Developer logo slot 5", src: null },
];

export type LandingTool = {
  name: string;
  eyebrow: string;
  description: string;
  href: string | null;
  fileTypes: string[];
  accent: "amber" | "clay" | "cream";
};

export const landingTools: LandingTool[] = [
  {
    name: "Paint Booth",
    eyebrow: "FiveM livery editor",
    description: "Preview liveries on 3D vehicles while you iterate on PSD, PDN, XCF, PNG, and texture exports.",
    href: "/paintbooth",
    fileTypes: [".psd", ".pdn", ".xcf", ".png"],
    accent: "amber",
  },
  {
    name: "Paint Booth Studio",
    eyebrow: "Browser texture editor + 3D",
    description: "Edit liveries directly in the browser with a Photoshop-style canvas docked to a live 3D vehicle preview.",
    href: "/paintbooth-studio",
    fileTypes: [".yft", ".psd", ".dds", ".png"],
    accent: "clay",
  },
  {
    name: "UV Studio",
    eyebrow: "GTA V vehicle tools",
    description: "Recover clean UV templates from mapped models when the original source files are missing.",
    href: "/uv-studio",
    fileTypes: [".yft", ".obj", ".png"],
    accent: "cream",
  },
  {
    name: "Blinklab",
    eyebrow: "Light pattern editor",
    description: "Build siren and lightbar patterns on an 8-color grid, preview timing, then export usable values.",
    href: "/blinklab",
    fileTypes: [".xml", ".dat", ".json"],
    accent: "clay",
  },
  {
    name: "EasyMeta",
    eyebrow: "FiveM meta generator",
    description: "Generate vehicles.meta, handling.meta, carvariations.meta, and carcols.meta with guided fields.",
    href: "/easy-meta",
    fileTypes: [".meta", ".xml"],
    accent: "amber",
  },
  {
    name: "PackBench",
    eyebrow: "FiveM resource builder",
    description: "Turn model, texture, and meta files into a clean resource ZIP with a generated fxmanifest.lua.",
    href: "/pack-bench",
    fileTypes: [".yft", ".ytd", ".lua", ".zip"],
    accent: "cream",
  },
  {
    name: "Code Smith",
    eyebrow: "Resource scanner",
    description: "Scan zipped resources for loop abuse, missing waits, heavy events, and unused asset clutter.",
    href: "/code-smith",
    fileTypes: [".lua", ".js", ".zip"],
    accent: "clay",
  },
  {
    name: "Script Documentation Generator",
    eyebrow: "Resource docs",
    description: "Generate README-ready docs from commands, exports, events, dependencies, and config files.",
    href: "/script-docs",
    fileTypes: [".lua", ".js", ".zip", ".md"],
    accent: "cream",
  },
  {
    name: "EUP Viewer",
    eyebrow: "Ped and outfit preview",
    description: "Drag in YDD assets and inspect EUP work in the browser before it reaches your server.",
    href: "/eup-viewer",
    fileTypes: [".ydd", ".ytd"],
    accent: "amber",
  },
  {
    name: "Addon EUP Creator",
    eyebrow: "EUP resource generator",
    description: "Build addon EUP packs with generated folders, manifests, and stream-ready structure.",
    href: "/addon-eup-creator",
    fileTypes: [".ydd", ".ytd", ".lua", ".zip"],
    accent: "clay",
  },
  {
    name: "Weapon Designer",
    eyebrow: "Weapon texture workflow",
    description: "Preview weapon texture passes while you iterate without falling into an in-game reload loop.",
    href: "/weapon-viewer",
    fileTypes: [".ydr", ".ytd", ".png"],
    accent: "cream",
  },
  {
    name: "ModelForge",
    eyebrow: "AI model workspace",
    description: "Generate, save, and manage browser-ready 3D model assets for creator workflows.",
    href: "/model-forge",
    fileTypes: [".glb", ".obj"],
    accent: "amber",
  },
  {
    name: "Asset Art",
    eyebrow: "AI inventory art",
    description: "Create polished FiveM inventory icons and item artwork with guided AI generation.",
    href: "/item-image",
    fileTypes: [".png", ".webp"],
    accent: "cream",
  },
  {
    name: "Badge Maker",
    eyebrow: "AI badge & emblem generator",
    description: "Generate crests, badges, and emblems for liveries, departments, and crews with guided AI.",
    href: "/badge-maker",
    fileTypes: [".png", ".webp"],
    accent: "amber",
  },
  {
    name: "Chevron Builder",
    eyebrow: "Rear warning pattern generator",
    description: "Generate print-ready rear warning chevrons for Fire, EMS, DOT, tow, and emergency vehicles with custom colors and angles.",
    href: "/chevron-builder",
    fileTypes: [".png"],
    accent: "clay",
  },
];

export const workflowSteps = [
  {
    title: "Upload and preview files",
    detail: "Drop FiveM assets directly into the browser workspace and inspect what you are about to ship.",
    command: "upload .yft + .ytd",
  },
  {
    title: "Generate UV/template",
    detail: "Recover missing templates and build texture guides for livery or model work.",
    command: "extract uv.png",
  },
  {
    title: "Configure lights",
    detail: "Preview light patterns in a live grid before exporting values for your vehicle setup.",
    command: "pattern -> binary",
  },
  {
    title: "Generate meta",
    detail: "Create vehicles.meta, handling.meta, carvariations.meta, and carcols.meta from guided inputs.",
    command: "build carcols",
  },
  {
    title: "Package and export",
    detail: "Organize files into a clean resource archive with the manifest and expected folder structure.",
    command: "export resource.zip",
  },
];

export const useCases = [
  {
    title: "Vehicle developers",
    copy: "Preview liveries, recover UVs, configure lighting, and generate GTA V vehicle meta without bouncing between local utilities.",
  },
  {
    title: "Server owners",
    copy: "Validate resources before they land on your server. Package consistently. Audit script quality before it ships.",
  },
  {
    title: "Livery and EUP creators",
    copy: "Move faster from texture edits to browser previews, then test clothing and vehicle visuals before handoff.",
  },
  {
    title: "Resource and weapon developers",
    copy: "Scan scripts, clean asset bundles, package exports, and review weapon texture work before it hits the server.",
  },
];

export type PricingTier = {
  name: string;
  tier: PlanTier;
  subtitle: string;
  monthly: string;
  annual: string;
  effectiveMonthly: string;
  includes: string[];
  /** Optional bonus line shown above the includes list (e.g. monthly AI token allowance). */
  highlight?: string;
  featured?: boolean;
};

export const pricingTiers: PricingTier[] = [
  {
    name: "Vehicle Developer",
    tier: "vehicle_dev",
    subtitle: "Paint, UV, lights, and meta — the full vehicle dev workbench.",
    monthly: "$14.99",
    annual: "$149.99",
    effectiveMonthly: "$12.50",
    includes: [
      "Paint Booth + live 3D vehicle preview",
      "UV Studio for template recovery",
      "Blinklab for siren and lightbar patterns",
      "EasyMeta for vehicles.meta, handling, and carcols",
      "Cloud project saving + 10 GB storage",
    ],
  },
  {
    name: "Server Developer",
    tier: "server_dev",
    subtitle: "Validation, packaging, EUP, and weapons — plus AI for icons and art.",
    monthly: "$19.99",
    annual: "$199.99",
    effectiveMonthly: "$16.67",
    highlight: "300 AI tokens / month",
    includes: [
      "PackBench, Code Smith, Code Visualizer, Script Docs, EUP Viewer, Addon EUP Creator",
      "Weapon Designer",
      "Asset Art for inventory icons",
      "ModelForge",
      "25 GB cloud storage",
    ],
  },
  {
    name: "Pro",
    tier: "all_services",
    subtitle: "Every Workshop tool. Vehicle, server, and creator workflows on one plan.",
    monthly: "$29.99",
    annual: "$299.99",
    effectiveMonthly: "$25.00",
    highlight: "750 AI tokens / month",
    includes: [
      "Everything in Vehicle Developer + Server Developer",
      "Paint Booth Studio with live preview",
      "More AI generations — 750 tokens/month vs 300",
      "50 GB cloud storage",
    ],
    featured: true,
  },
  {
    name: "Studio",
    tier: "studio",
    subtitle: "Higher limits, priority AI, and early access. For full-time creators.",
    monthly: "$49.99",
    annual: "$499.99",
    effectiveMonthly: "$41.66",
    highlight: "2,000 AI tokens / month",
    includes: [
      "Everything in Pro",
      "Priority AI generation queue",
      "Advanced cloud project saving",
      "Early access to new tools",
      "150 GB cloud storage",
    ],
  },
];

export const faqItems = [
  {
    question: "What is The Workshop?",
    answer:
      "A browser-based workspace for FiveM creators. Tools for liveries, EUP, vehicle meta, scripts, and resource packaging. No installs. One subscription.",
  },
  {
    question: "Is anything free to try?",
    answer: "Yes — Paint Booth has a free demo. The full toolset requires a subscription.",
  },
  {
    question: "Can I generate vehicle meta files?",
    answer:
      "Yes. EasyMeta generates vehicles.meta, handling.meta, carvariations.meta, and carcols.meta from guided fields.",
  },
  {
    question: "Does this replace local GTA V vehicle tools?",
    answer:
      "For most workflows, yes — livery preview, UV extraction, light pattern editing, meta generation, and packaging all run in the browser. Niche local-only utilities are still better at the edges.",
  },
  {
    question: "Can I build a complete FiveM resource?",
    answer:
      "Yes. PackBench takes your model, texture, and meta files and outputs a structured resource ZIP with fxmanifest.lua.",
  },
  {
    question: "How does billing work?",
    answer: "Subscriptions run through Shopify. Manage, switch, or cancel from your Shopify account.",
  },
  {
    question: "I subscribed but it's not showing on my account.",
    answer:
      "Access links by email. Make sure your Workshop account uses the same email you checked out with — if they differ, sign in (or sign up) with the purchase email and your subscription will appear. Still stuck? Reach out and we'll link it for you.",
  },
];
